import { environmentManager } from '@tanstack/react-query';
import { createServerOnlyFn } from '@tanstack/react-start';
import message from 'antd/es/message';
import type { AxiosResponse } from 'axios';
import Axios from 'axios';
import queryString from 'query-string';

export const apiClient = Axios.create({
  baseURL: environmentManager.isServer() ? undefined : '/rest',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: (params) => queryString.stringify(params),
});

const getServerRequestConfig = createServerOnlyFn(async () => {
  const [{ getRequestUrl }, { Agent }] = await Promise.all([
    import('@tanstack/react-start/server'),
    import('node:https'),
  ]);

  const requestUrl = getRequestUrl();
  const httpsAgent =
    process.env.NODE_ENV === 'development'
      ? new Agent({
          rejectUnauthorized: false,
        })
      : undefined;

  return {
    baseURL: `${requestUrl.origin}/rest`,
    httpsAgent,
  };
});

export class ResponseError extends Error {
  response?: AxiosResponse;

  jsonResponse: any;

  textResponse: string;

  displayMessage: string;

  status?: number;

  constructor(msg: string, response?: AxiosResponse) {
    super(msg);
    this.response = response;
    this.jsonResponse = {};
    this.textResponse = '';
    this.displayMessage = msg;
  }
}

const getErrorDisplayMessage = (data: any): string | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (typeof data.title === 'string' && data.title.trim()) {
    return data.title;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const [firstError] = data.errors;
    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }
  }

  if (typeof data.errors === 'string' && data.errors.trim()) {
    return data.errors;
  }

  return undefined;
};

apiClient.interceptors.request.use(async (config) => {
  if (environmentManager.isServer()) {
    const serverRequestConfig = await getServerRequestConfig();
    config.baseURL = config.baseURL ?? serverRequestConfig.baseURL;
    config.httpsAgent = config.httpsAgent ?? serverRequestConfig.httpsAgent;
  }

  // No extra configuration for absolute URLs (i.e.: a file in S3)
  if (config.url!.charAt(0) !== '/') {
    return config;
  }

  config.headers = config.headers ?? {};

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorText = `Unhandled status: ${error.response?.status}`;

    if (error.response?.status === 500) {
      errorText = 'Internal Server Error';
    }

    if (error.response?.status === 400) {
      errorText = 'Bad Request';
    }

    if (error.response?.status === 403) {
      errorText = 'Missing permission';
    }

    if (error.response?.status === 504) {
      errorText = 'Gateway timeout';
      message.error({
        content: 'Error communicating with remote server. Please check your internet connection.',
        // cspell:ignore NETGONE
        key: 'NETGONE',
      });
    }

    const responseError = new ResponseError(errorText, error.response);

    responseError.status = error.response?.status;
    if (error.response?.data instanceof Object) {
      responseError.jsonResponse = error.response!.data;
      const displayMessage = getErrorDisplayMessage(responseError.jsonResponse);
      if (displayMessage) {
        responseError.displayMessage = displayMessage;
      }
    } else {
      responseError.textResponse = error.response?.data as string;
    }
    throw responseError;
  },
);
